import { FC } from "react";
import { camelCase, upperFirst } from "lodash";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Skeleton,
  Alert,
} from "@mui/material";
import Reviews from "@mui/icons-material/Reviews";
import Markdown from "react-markdown";
// Use hook that extracts data from snapshot to avoid API calls
import useRecommendationFromSnapshot from "../../hooks/useRecommendationFromSnapshot";
import { RoadmapItemDto } from "../../../../../../customer-experience/src/lib/ShiOneClient";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { programToString } from "../../../../../../customer-experience/src/v2/src/features/assessments-feature/helpers/assessmentWidgetHelpers";

interface AssessmentRecommendationDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  recommendation: RoadmapItemDto | null;
}

const AssessmentRecommendationDetailsDialog: FC<
  AssessmentRecommendationDetailsDialogProps
> = ({ open, onClose, recommendation }) => {
  // Use the snapshot data instead of making an API call
  const {
    data: apiRecommendation,
    isError,
    isLoading,
  } = useRecommendationFromSnapshot(recommendation);

  const displayData = apiRecommendation || {
    capability: recommendation?.name,
    recommendationsSummary: recommendation?.description || "",
    recommendations: recommendation?.description || "",
    impact: recommendation?.sourceMetadata?.impact,
    category: recommendation?.sourceMetadata?.category,
    subcategory: recommendation?.sourceMetadata?.subcategory,
    otherToolsLogos: [],
  };

  const getImpactChipColor = (impactLevel: string | undefined) => {
    if (impactLevel === "High") return "error";
    if (impactLevel === "Medium") return "warning";
    if (impactLevel === "Low") return "success";
    return "default";
  };

  const toPascalCase = (input: string) => {
    return upperFirst(camelCase(input));
  };

  const assessmentId = recommendation?.sourceMetadata?.assessmentId;
  const navigationLink =
    apiRecommendation?.program && assessmentId
      ? `/assessments/${programToString(toPascalCase(apiRecommendation.program))}/recommendations?aid=${assessmentId}`
      : "/assessments";

  const recommendationsMatchSummary =
    displayData.recommendations?.trim() ===
    displayData.recommendationsSummary?.trim();

  const markdownStyles = {
    "& p": {
      margin: 0,
      marginBottom: 1,
      color: "text.secondary",
      fontSize: "0.875rem",
    },
    "& ul, & ol": {
      marginTop: 0,
      marginBottom: 1,
      paddingLeft: 3,
      color: "text.secondary",
      fontSize: "0.875rem",
    },
    "& li": {
      marginBottom: 0.5,
    },
    "& strong": {
      color: "text.primary",
      fontWeight: 600,
    },
  };

  const renderDialogContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="text" height={60} />
          <Skeleton variant="text" height={200} />
        </Box>
      );
    }

    if (isError && !recommendation) {
      return (
        <Alert severity="error">
          Failed to load recommendation details. Please try again.
        </Alert>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box
          sx={{
            backgroundColor: "action.hover",
            mx: -3,
            mt: -3,
            px: 3,
            pt: 3,
            pb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            {displayData.category && (
              <Chip
                label={displayData.category}
                variant="outlined"
                size="small"
                sx={{ borderColor: "divider" }}
              />
            )}
            {displayData.subcategory && (
              <Chip
                label={displayData.subcategory}
                variant="outlined"
                size="small"
                sx={{ borderColor: "divider" }}
              />
            )}
            {displayData.impact && (
              <Chip
                label={`Impact: ${displayData.impact}`}
                sx={{ borderColor: getImpactChipColor(displayData.impact) }}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
          {displayData.capability && (
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {displayData.capability}
            </Typography>
          )}
        </Box>

        {displayData.recommendationsSummary && (
          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Summary
            </Typography>
            <Box sx={markdownStyles}>
              <Markdown>{displayData.recommendationsSummary}</Markdown>
            </Box>
          </Box>
        )}

        <Divider />

        {displayData.recommendations && !recommendationsMatchSummary && (
          <Box>
            <Box sx={markdownStyles}>
              <Markdown>{displayData.recommendations}</Markdown>
            </Box>
          </Box>
        )}

        {displayData.otherToolsLogos &&
          Array.isArray(displayData.otherToolsLogos) &&
          displayData.otherToolsLogos.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Related Tools
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center",
                  p: 2,
                  backgroundColor: "action.hover",
                  borderRadius: 1,
                }}
              >
                {displayData.otherToolsLogos.map((logoUrl) => (
                  <Box
                    key={logoUrl}
                    component="img"
                    src={logoUrl}
                    alt={`Tool Logo`}
                    sx={{
                      height: 40,
                      width: "auto",
                      maxWidth: 120,
                      objectFit: "contain",
                      filter: "grayscale(0.2)",
                      transition: "filter 0.2s",
                      "&:hover": {
                        filter: "grayscale(0)",
                      },
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

        <Box
          sx={{
            backgroundColor: "action.hover",
            borderRadius: 1,
            p: 2,
            mt: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body2">
            For complete assessment details, view your{" "}
            <Button
              href={navigationLink}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none",
                p: 0,
                minWidth: "auto",
                verticalAlign: "baseline",
              }}
            >
              Assessments
            </Button>
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Reviews sx={{ color: "text.secondary" }} />
          <Typography variant="h6">
            Assessment Recommendation Details
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>{renderDialogContent()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssessmentRecommendationDetailsDialog;
